import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, GripVertical } from "lucide-react";
import { toLocalDateStr, formatTime } from "./pulseUtils";

export const PulseCalendar = ({ currentDate, selectedDate, events, tasks, onNavigateMonth, onGoToToday, onSelectDate, onEventDrop }) => {
  const { t } = useTranslation();
  const [dragOverDate, setDragOverDate] = useState(null);

  const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startPadding = firstDay.getDay();
    startPadding = startPadding === 0 ? 6 : startPadding - 1;
    const days = [];

    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  }, [currentDate]);

  const getEventsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return events.filter(e => toLocalDateStr(new Date(e.start_datetime)) === dateStr);
  };

  const getTasksForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return tasks.filter(tk => {
      if (!tk.due_datetime || !tk.show_on_calendar) return false;
      return toLocalDateStr(new Date(tk.due_datetime)) === dateStr;
    });
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isSelected = (date) => date.toDateString() === selectedDate.toDateString();

  const handleDragStart = useCallback((e, event) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", JSON.stringify({
      eventId: event.id,
      startDatetime: event.start_datetime,
      endDatetime: event.end_datetime,
      title: event.title,
    }));
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const dateStr = toLocalDateStr(date);
    setDragOverDate(dateStr);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverDate(null);
  }, []);

  const handleDrop = useCallback((e, targetDate) => {
    e.preventDefault();
    setDragOverDate(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (!data.eventId) return;

      const oldStart = new Date(data.startDatetime);
      const oldEnd = new Date(data.endDatetime);

      // Preserve time, change date
      const newStart = new Date(targetDate);
      newStart.setHours(oldStart.getHours(), oldStart.getMinutes(), 0, 0);

      const newEnd = new Date(targetDate);
      newEnd.setHours(oldEnd.getHours(), oldEnd.getMinutes(), 0, 0);

      // If the event spans overnight, adjust end date proportionally
      if (oldEnd.getDate() !== oldStart.getDate()) {
        const dayDiff = Math.round((oldEnd - oldStart) / (1000 * 60 * 60 * 24));
        newEnd.setDate(newEnd.getDate() + dayDiff);
      }

      // Don't fire if dropped on the same date
      if (toLocalDateStr(oldStart) === toLocalDateStr(targetDate)) return;

      if (onEventDrop) {
        onEventDrop(data.eventId, newStart, newEnd);
      }
    } catch {
      // Invalid drag data
    }
  }, [onEventDrop]);

  const isDragOver = (date) => dragOverDate === toLocalDateStr(date);
  const isRecurringInstance = (event) => event.id && event.id.includes("_r");

  const monthLabel = t(`pulsePlanner.months.${currentDate.getMonth() + 1}`);

  return (
    <div className="space-y-4">
      {/* Calendar Controls */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onNavigateMonth(-1)} className="rounded-sm h-8 w-8 p-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onGoToToday} className="rounded-sm h-8 px-3">
            {t("pulsePlanner.today")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => onNavigateMonth(1)} className="rounded-sm h-8 w-8 p-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <h2 className="font-display text-lg font-semibold text-slate-900 uppercase tracking-tight">
          {monthLabel} {currentDate.getFullYear()}
        </h2>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {DAY_KEYS.map(key => (
            <div key={key} className="p-2 text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
              {t(`pulsePlanner.daysShort.${key}`)}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, idx) => {
            const dayEvents = getEventsForDate(day.date);
            const dayTasks = getTasksForDate(day.date);

            return (
              <div
                key={idx}
                onClick={() => onSelectDate(day.date)}
                onDragOver={(e) => handleDragOver(e, day.date)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, day.date)}
                className={`min-h-[72px] p-1.5 border-b border-r border-slate-100 cursor-pointer transition-all ${
                  !day.isCurrentMonth ? 'bg-slate-50/50' : 'hover:bg-slate-50'
                } ${isSelected(day.date) ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200' : ''}
                ${isDragOver(day.date) ? 'bg-indigo-100/70 ring-2 ring-inset ring-indigo-400 scale-[1.02]' : ''}`}
              >
                <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                  isToday(day.date)
                    ? 'bg-indigo-600 text-white'
                    : isSelected(day.date)
                      ? 'text-indigo-600'
                      : day.isCurrentMonth
                        ? 'text-slate-700'
                        : 'text-slate-400'
                }`}>
                  {day.date.getDate()}
                </div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 2).map(event => {
                    const isRecurring = isRecurringInstance(event);
                    return (
                      <div
                        key={event.id}
                        draggable={!isRecurring}
                        onDragStart={!isRecurring ? (e) => handleDragStart(e, event) : undefined}
                        className={`text-[10px] px-1 py-0.5 bg-indigo-100 text-indigo-700 rounded truncate flex items-center gap-0.5 ${
                          !isRecurring ? 'cursor-grab active:cursor-grabbing hover:bg-indigo-200' : 'opacity-80'
                        }`}
                        title={`${event.title} - ${formatTime(event.start_datetime)}${isRecurring ? ` (${t("pulsePlanner.recurring")})` : ` - ${t("pulsePlanner.dragToReschedule")}`}`}
                      >
                        {!isRecurring && <GripVertical className="w-2.5 h-2.5 flex-shrink-0 opacity-40" />}
                        <span className="truncate">{formatTime(event.start_datetime)}</span>
                      </div>
                    );
                  })}
                  {dayTasks.slice(0, 2 - dayEvents.length).map(task => (
                    <div key={task.id} className={`text-[10px] px-1 py-0.5 rounded truncate ${
                      task.priority === 'high' ? 'bg-red-100 text-red-700' :
                      task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {formatTime(task.due_datetime)}
                    </div>
                  ))}
                  {(dayEvents.length + dayTasks.length) > 2 && (
                    <div className="text-[10px] text-slate-400 px-1">
                      +{dayEvents.length + dayTasks.length - 2}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
